import AT from './actiontypes';
import { IHistorical, IHistoricalData, ILog } from './model';
import { IHistoricalDefault } from './defaults';
import {
  areEqualILog,
  AreEqualILog,
  msgErrorFetching,
  removeLog,
  updateIHistorical,
} from './utils';
import * as CallStatus from '../../utils/callStatus';
import { MessageType } from '../../core/localization/model';
import _ from 'lodash';

export const remove = <T extends ILog>(
  state: IHistorical<T>,
  data: T,
  areEqual: AreEqualILog<T> = areEqualILog
): IHistorical<T> => {
  const updatedData = removeLog(state, data, areEqual);
  return { ...state, ...updatedData };
};

export const moreFetch = <T extends ILog>(
  state: IHistorical<T>
): IHistorical<T> => {
  const moreStatus = CallStatus.fetch(state.moreStatus);
  return { ...state, moreStatus };
};

export const moreSuccess = <T extends ILog>(
  state: IHistorical<T>,
  data: IHistoricalData<T>,
  areEqual: AreEqualILog<T> = areEqualILog
): IHistorical<T> => {
  const updatedData = updateIHistorical(state, data, areEqual);
  const moreStatus = CallStatus.success(state.moreStatus);
  return { ...state, ...updatedData, moreStatus };
};

export const moreError = <T extends ILog>(
  state: IHistorical<T>,
  errorMsg = msgErrorFetching
): IHistorical<T> => {
  const total = _.max([state.total, 0]) || 0;
  const moreStatus = CallStatus.error(state.moreStatus, errorMsg);
  return { ...state, total, moreStatus };
};

export const latestFetch = <T extends ILog>(
  state: IHistorical<T>
): IHistorical<T> => {
  const latestStatus = CallStatus.fetch(state.latestStatus);
  return { ...state, latestStatus };
};

export const latestSuccess = <T extends ILog>(
  state: IHistorical<T>,
  data: IHistoricalData<T>,
  areEqual: AreEqualILog<T> = areEqualILog
): IHistorical<T> => {
  const updatedData = updateIHistorical(state, data, areEqual, true);
  const latestStatus = CallStatus.success(state.latestStatus);
  return { ...state, ...updatedData, latestStatus };
};

export const latestError = <T extends ILog>(
  state: IHistorical<T>,
  errorMsg = msgErrorFetching
): IHistorical<T> => {
  const total = _.max([state.total, 0]) || 0;
  const latestStatus = CallStatus.error(state.latestStatus, errorMsg);
  return { ...state, total, latestStatus };
};

const reducer = <T extends ILog>(
  state = IHistoricalDefault<T>(),
  action: any
) => {
  switch (action.type) {
    case AT.HISTORICAL_MORE_FETCH: {
      return moreFetch(state);
    }

    case AT.HISTORICAL_MORE_SUCCESS: {
      const historicalData: IHistoricalData<T> = action.payload;
      return moreSuccess(state, historicalData);
    }

    case AT.HISTORICAL_MORE_ERROR: {
      const errorMessage: MessageType = action.payload;
      return moreError(state, errorMessage);
    }

    case AT.HISTORICAL_LATEST_FETCH: {
      return latestFetch(state);
    }

    case AT.HISTORICAL_LATEST_SUCCESS: {
      const historicalData: IHistoricalData<T> = action.payload;
      return latestSuccess(state, historicalData);
    }

    case AT.HISTORICAL_LATEST_ERROR: {
      const errorMessage: MessageType = action.payload;
      return latestError(state, errorMessage);
    }

    default: {
      return state;
    }
  }
};

export default reducer;
